import { useCallback, useEffect, useRef, useState } from 'react';
import { MessagingService } from '../services';
import { AxiosMessagingService } from '../services/axiosMessagingService';
import type {
  ChatConfig,
  Message,
  MessagingHookResult,
  ParticipantRole,
} from '../types';
import { useStorage } from './useStorage';

export interface MessagingOptions {
  config: ChatConfig;
  typingTimeout?: number; // Milliseconds before typing indicator auto-stops
}

/**
 * Custom hook for handling real-time messaging with server-sent events
 *
 * @param options - Messaging configuration options
 * @returns Messaging functions and state
 */
export function useMessaging(options: MessagingOptions): MessagingHookResult {
  const { config, typingTimeout = 3000 } = options;
  const [messages, setMessages] = useState<Message[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [participantTyping, setParticipantTyping] = useState<
    Record<string, boolean>
  >({});

  const messagingServiceRef = useRef<MessagingService | null>(null);
  const axiosMessagingServiceRef = useRef<AxiosMessagingService | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationIdRef = useRef<string | null>(null);

  const storage = useStorage({
    type: 'localStorage',
    prefix: 'salesforce_messaging',
  });

  // Initialize services
  useEffect(() => {
    messagingServiceRef.current = new MessagingService(config);
    axiosMessagingServiceRef.current = new AxiosMessagingService(config);
  }, [config]);

  const sendMessage = useCallback(async (content: string): Promise<void> => {
    if (!messagingServiceRef.current || !conversationIdRef.current) {
      throw new Error(
        'Messaging service not initialized or no active conversation'
      );
    }

    const tempMessage: Message = {
      id: `temp_${Date.now()}`,
      conversationId: conversationIdRef.current,
      content,
      sender: 'customer' as ParticipantRole,
      timestamp: new Date(),
      messageType: 'text',
      deliveryStatus: 'sending',
    };

    // Optimistically add message to UI
    setMessages((previousMessages) => [...previousMessages, tempMessage]);

    try {
      // Try axios service first, fallback to original messaging service
      if (axiosMessagingServiceRef.current) {
        try {
          await axiosMessagingServiceRef.current.sendMessage(
            conversationIdRef.current,
            content
          );
        } catch (axiosError) {
          console.warn(
            'Axios send failed, falling back to original service:',
            axiosError
          );
          await messagingServiceRef.current.sendTextMessage(
            conversationIdRef.current,
            content
          );
        }
      } else {
        await messagingServiceRef.current.sendTextMessage(
          conversationIdRef.current,
          content
        );
      }

      // Mark temp message as delivered
      setMessages((previousMessages) =>
        previousMessages.map((message) =>
          message.id === tempMessage.id
            ? { ...message, deliveryStatus: 'delivered' }
            : message
        )
      );
    } catch (error) {
      // Mark message as failed
      setMessages((previousMessages) =>
        previousMessages.map((message) =>
          message.id === tempMessage.id
            ? { ...message, deliveryStatus: 'failed' }
            : message
        )
      );
      throw error;
    }
  }, []);

  const setTypingIndicator = useCallback(
    (typing: boolean): void => {
      setIsTyping(typing);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      if (typing) {
        // Auto-stop typing after timeout
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
        }, typingTimeout);

        // TODO: Send typing indicator to server when API supports it
        // messagingServiceRef.current?.sendTypingIndicator(conversationIdRef.current, true);
      }
    },
    [typingTimeout]
  );

  const connect = useCallback((conversationId: string): void => {
    conversationIdRef.current = conversationId;

    // Store conversation ID for session continuity
    storage.setItem('currentConversationId', conversationId);

    // Get JWT from storage for authentication
    const jwt = storage.getItem<string>('jwt');

    if (!jwt) {
      console.error('No JWT found for messaging connection');
      setIsConnected(false);
      return;
    }

    if (!axiosMessagingServiceRef.current) {
      console.error('Axios messaging service not initialized');
      setIsConnected(false);
      return;
    }

    // eslint-disable-next-line no-console
    console.log('🔌 Starting axios-based messaging connection...');

    // Configure the axios service
    axiosMessagingServiceRef.current.setJWT(jwt);
    axiosMessagingServiceRef.current.setConversationId(conversationId);

    // Test connection and start polling
    axiosMessagingServiceRef.current
      .testConnection()
      .then((result) => {
        if (result.success) {
          // eslint-disable-next-line no-console
          console.log('✅ Axios messaging connection successful');
          setIsConnected(true);

          // Start polling for messages
          if (axiosMessagingServiceRef.current) {
            axiosMessagingServiceRef.current.startPolling((message) => {
              setMessages((previousMessages) => {
                // Avoid duplicates
                const existingMessage = previousMessages.find(
                  (msg) => msg.id === message.id
                );
                if (existingMessage) {
                  return previousMessages;
                }
                return [...previousMessages, message];
              });
            });
          }
        } else {
          console.error('❌ Axios messaging connection failed:', result.error);
          setIsConnected(false);
        }
      })
      .catch((error) => {
        console.error('❌ Failed to test messaging connection:', error);
        setIsConnected(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnect = useCallback((): void => {
    if (axiosMessagingServiceRef.current) {
      axiosMessagingServiceRef.current.stopPolling();
    }

    setIsConnected(false);
    conversationIdRef.current = null;
    setMessages([]);
    setParticipantTyping({});

    // Clear stored conversation ID
    storage.removeItem('currentConversationId');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  // Restore connection on mount if there's a stored conversation ID
  useEffect(() => {
    const storedConversationId = storage.getItem<string>(
      'currentConversationId'
    );
    if (storedConversationId && !conversationIdRef.current) {
      connect(storedConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  return {
    messages,
    sendMessage,
    isConnected,
    isTyping,
    participantTyping,
    setTyping: setTypingIndicator,
    connect,
    disconnect,
  };
}
